import React, { useState, useMemo } from "react";
import { formatINR } from "../data/dummyMonthlyLedgerData";
import {
  FaFileExcel,
  FaFilePdf,
  FaPrint,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { TableRowsSkeleton } from "../../../Component/Skeleton";

export default function MonthlyLedgerTable({
  data = [],
  totals = { debit: 0, credit: 0, closingBalance: 0, closingType: "Dr" },
  openingBalance = 0,
  openingType = "Dr",
  onSelectMonth,
  onExportExcel,
  onExportPDF,
  onPrint,
  loading = false,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(12);

  const totalPages = Math.ceil(data.length / rowsPerPage) || 1;
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return data.slice(start, start + rowsPerPage);
  }, [data, currentPage, rowsPerPage]);

  return (
    <div className="mls_table_card">
      <div className="mls_table_header">
        <h3 className="mls_table_title">Month Wise Summary</h3>
        <div className="mls_table_actions">
          <button
            type="button"
            className="mls_export_btn btn_excel"
            onClick={onExportExcel}
            title="Export to Microsoft Excel"
          >
            <FaFileExcel />
            Export Excel
          </button>
          <button
            type="button"
            className="mls_export_btn btn_pdf"
            onClick={onExportPDF}
            title="Export to PDF"
          >
            <FaFilePdf />
            Export PDF
          </button>
          <button
            type="button"
            className="mls_export_btn btn_print"
            onClick={onPrint}
            title="Print Summary"
          >
            <FaPrint />
            Print
          </button>
        </div>
      </div>

      <div className="mls_table_wrapper">
        <table className="mls_custom_table">
          <thead>
            <tr>
              <th style={{ width: "28%" }}>Month</th>
              <th className="mls_table_num" style={{ width: "24%" }}>
                Debit (₹)
              </th>
              <th className="mls_table_num" style={{ width: "24%" }}>
                Credit (₹)
              </th>
              <th className="mls_table_num" style={{ width: "24%" }}>
                Closing Balance (₹)
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Opening Balance Line */}
            <tr className="mls_opening_row">
              <td style={{ fontWeight: 700, color: "#1e293b" }}>
                Opening Balance
              </td>
              <td className="mls_table_num">
                {openingType === "Dr" && Number(openingBalance) > 0 ? (
                  <span style={{ fontWeight: 600 }}>{formatINR(openingBalance)}</span>
                ) : (
                  ""
                )}
              </td>
              <td className="mls_table_num">
                {openingType === "Cr" && Number(openingBalance) > 0 ? (
                  <span style={{ fontWeight: 600 }}>{formatINR(openingBalance)}</span>
                ) : (
                  ""
                )}
              </td>
              <td className="mls_table_num"></td>
            </tr>

            {/* Loading State */}
            {loading && <TableRowsSkeleton columns={4} rows={6} />}

            {/* Empty State */}
            {!loading && data.length === 0 && (
              <tr>
                <td colSpan="4" style={{ textAlign: "center", padding: "20px", color: "#a0aec0" }}>
                  No transactions found for the selected period.
                </td>
              </tr>
            )}

            {/* Month-wise Rows */}
            {!loading &&
              paginatedData.map((row, idx) => {
                const isCr = row.balanceType === "Cr";
                return (
                  <tr
                    key={row.monthKey || idx}
                    className="clickable_row"
                    onClick={() => onSelectMonth && onSelectMonth(row)}
                    title="Click to view detailed transactions for this month"
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <span className="mls_month_badge">
                        {row.month}
                      </span>
                    </td>
                    <td className="mls_table_num">
                      {formatINR(row.debit)}
                    </td>
                    <td className="mls_table_num">
                      {formatINR(row.credit)}
                    </td>
                    <td className="mls_table_num">
                      {Number(row.closingBalance) !== 0 ? (
                        <>
                          <span style={{ fontWeight: 600 }}>{formatINR(Math.abs(row.closingBalance))}</span>
                          <span className={isCr ? "drcr_text_cr" : "drcr_text_dr"}>
                            {" "}{row.balanceType || "Dr"}
                          </span>
                        </>
                      ) : (
                        ""
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
          <tfoot>
            <tr className="mls_table_total_row">
              <td>Total</td>
              <td className="mls_table_num">
                {formatINR(totals.debit)}
              </td>
              <td className="mls_table_num">
                {formatINR(totals.credit)}
              </td>
              <td className="mls_table_num">
                <span style={{ fontWeight: 700 }}>{formatINR(totals.closingBalance)}</span>
                <span className={totals.closingType === "Cr" ? "drcr_text_cr" : "drcr_text_dr"}>
                  {" "}{totals.closingType || "Dr"}
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="mls_pagination_row">
        <div>
          Showing {data.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1} to{" "}
          {Math.min(currentPage * rowsPerPage, data.length)} of {data.length} records
        </div>

        <div className="mls_page_controls">
          <button
            type="button"
            className="mls_page_btn"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          >
            <FaChevronLeft style={{ fontSize: "9px" }} />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((pg) => (
            <button
              key={pg}
              type="button"
              className={`mls_page_btn ${currentPage === pg ? "active" : ""}`}
              onClick={() => setCurrentPage(pg)}
            >
              {pg}
            </button>
          ))}
          <button
            type="button"
            className="mls_page_btn"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
          >
            <FaChevronRight style={{ fontSize: "9px" }} />
          </button>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span>Rows per page:</span>
          <select
            className="mls_rows_select"
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={6}>6</option>
            <option value={12}>12</option>
            <option value={24}>24</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>
    </div>
  );
}
